import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { KeyRound, Loader2, Pencil, Search, Trash2, Undo2, UserX, UserCheck, Users } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { DetailDrawer, EmptyState, Field, Panel, StatusBadge, formatDateTime } from "@/components/admin/ui";
import { usePolling } from "@/hooks/usePolling";
import { usePageLastUpdated } from "@/context/PageMetaContext";
import { ROLE_LABELS } from "@/lib/roles";

const authLabel = (user) => user.auth_method || (user.auth_provider === "google" ? "Google" : "Password");
const ORG_ROLES = ["org_owner", "org_admin", "billing_admin", "team_lead", "user"];

export default function AdminUsers() {
  const { API } = useAuth();
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "", org_id: "" });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [statusBusy, setStatusBusy] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    if (!silent) setError("");
    try {
      const params = search.trim() ? { search: search.trim() } : {};
      const { data } = await axios.get(`${API}/admin/users`, { params, withCredentials: true });
      setUsers(data.users || []);
      setLastUpdated(Date.now());
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [API, search]);

  useEffect(() => {
    const timer = window.setTimeout(load, search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search]);

  useEffect(() => {
    axios.get(`${API}/admin/organizations`, { withCredentials: true }).then(({ data }) => setOrgs(data.organizations || [])).catch(() => {});
  }, [API]);

  // Skip the silent auto-refresh while an edit, status change, or delete is in flight.
  usePolling(() => {
    if (editing || saving || statusBusy || deleting) return;
    load(true);
  }, 10000);

  const loadDeleted = useCallback(async () => {
    setDeletedLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users/deleted`, { withCredentials: true });
      setDeletedUsers(data.deleted_users || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeletedLoading(false);
    }
  }, [API]);

  useEffect(() => { if (showDeleted) loadDeleted(); }, [showDeleted, loadDeleted]);

  const openDetail = async (user) => {
    setSelected(user);
    setDetail(null);
    setResetMessage("");
    setEditing(false);
    setSaveError("");
    setDeleteConfirm(false);
    setDeleteError("");
    try {
      const { data } = await axios.get(`${API}/admin/users/${user.user_id}`, { withCredentials: true });
      setDetail(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const startEdit = () => {
    setEditForm({
      name: detail.user.name || "",
      email: detail.user.email || "",
      role: detail.user.role || "",
      org_id: detail.user.org_id || "",
    });
    setSaveError("");
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    setSaveError("");
    try {
      await axios.patch(`${API}/admin/users/${selected.user_id}`, editForm, { withCredentials: true });
      setEditing(false);
      await openDetail(selected);
      await load(true);
    } catch (err) {
      setSaveError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    setStatusBusy(true);
    setError("");
    try {
      await axios.patch(`${API}/admin/users/${selected.user_id}/status`, { disabled: detail.user.status !== "Disabled" }, { withCredentials: true });
      await openDetail(selected);
      await load(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setStatusBusy(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await axios.delete(`${API}/admin/users/${selected.user_id}`, { withCredentials: true });
      setDeleteConfirm(false);
      setSelected(null);
      setDetail(null);
      await load(true);
    } catch (err) {
      setDeleteError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const restore = async (deletedId) => {
    setRestoringId(deletedId);
    try {
      await axios.post(`${API}/admin/users/deleted/${deletedId}/restore`, {}, { withCredentials: true });
      await loadDeleted();
      await load(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRestoringId(null);
    }
  };

  const sendPasswordReset = async () => {
    setResetting(true);
    setResetMessage("");
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/users/${selected.user_id}/send-password-reset`, {}, { withCredentials: true });
      setResetMessage(data.message);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setResetting(false);
    }
  };

  const canManage = detail && !detail.user.is_platform_admin;

  usePageLastUpdated(lastUpdated, "users-last-updated");

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setShowDeleted((v) => !v)}
          data-testid="users-toggle-deleted-btn"
          className="text-sm font-bold text-blue-600 hover:underline dark:text-blue-400"
        >
          {showDeleted ? "Hide recently deleted" : "Recently deleted"}
        </button>
      </div>

      {showDeleted && (
        <Panel title="Recently Deleted Users" subtitle="Hard-deleted accounts can be restored here." testId="deleted-users-panel">
          {deletedLoading ? (
            <p className="text-sm font-semibold text-slate-500 dark:text-slate-400" data-testid="deleted-users-loading">Loading…</p>
          ) : deletedUsers.length === 0 ? (
            <EmptyState icon={Users} title="No deleted users." message="Accounts removed by a Platform Admin will appear here." testId="deleted-users-empty" />
          ) : (
            <div className="space-y-2" data-testid="deleted-users-list">
              {deletedUsers.map((d) => (
                <div key={d.deleted_id} className="flex items-center justify-between rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid={`deleted-user-row-${d.deleted_id}`}>
                  <div>
                    <p className="font-bold text-slate-950 dark:text-white">{d.name || d.email}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{d.email} · {ROLE_LABELS[d.role_at_deletion] || d.role_at_deletion} · {d.organization_name || "—"}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500">Deleted {formatDateTime(d.deleted_at)} by {d.deleted_by_name}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => restore(d.deleted_id)}
                    disabled={restoringId === d.deleted_id}
                    data-testid={`deleted-user-restore-${d.deleted_id}`}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-white disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
                  >
                    {restoringId === d.deleted_id ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />} Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      <Panel testId="users-table-panel">
        <label className="relative block max-w-md">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="user-search-input"
            placeholder="Search users or emails…"
            className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-4 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
          />
        </label>
        {error && <p className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="users-error">{error}</p>}
        <div className="mt-6">
          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="users-loading">Loading users…</div>
          ) : users.length === 0 ? (
            <EmptyState icon={Users} title="No users found." message="Authorized organization users will appear here." testId="users-empty" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] text-left text-sm" data-testid="users-table">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.12em] text-slate-400 dark:border-white/10 dark:text-slate-500">
                    <th className="py-3 pr-4 font-bold">User</th>
                    <th className="py-3 pr-4 font-bold">Email</th>
                    <th className="py-3 pr-4 font-bold">Organization</th>
                    <th className="py-3 pr-4 font-bold">Role</th>
                    <th className="py-3 pr-4 font-bold">Authentication</th>
                    <th className="py-3 pr-4 font-bold">Status</th>
                    <th className="py-3 pr-4 font-bold">Last Login</th>
                    <th className="py-3 font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.user_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 dark:border-white/5 dark:hover:bg-white/5" data-testid={`user-row-${user.user_id}`}>
                      <td className="py-4 pr-4 font-bold text-slate-950 dark:text-white">{user.name || "—"}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{user.email}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{user.organization_name || (user.is_platform_admin ? "Nova Opsis Platform" : "—")}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{user.is_platform_admin ? "Platform Admin" : ROLE_LABELS[user.role] || user.role || "—"}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400" data-testid={`user-auth-method-${user.user_id}`}>{authLabel(user)}</td>
                      <td className="py-4 pr-4"><StatusBadge status={user.status} testId={`user-status-${user.user_id}`} /></td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{formatDateTime(user.last_login_at)}</td>
                      <td className="py-4"><button type="button" onClick={() => openDetail(user)} data-testid={`user-view-${user.user_id}`} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-white dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Panel>

      <DetailDrawer
        open={!!selected}
        onClose={() => { setSelected(null); setDetail(null); }}
        title={selected?.name || selected?.email || "User"}
        subtitle="Platform user details"
        testId="user-drawer"
      >
        {selected && !detail ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="user-detail-loading">Loading user…</div>
        ) : detail && (
          <div className="space-y-7" data-testid="user-detail">
            {editing ? (
              <section className="space-y-4" data-testid="user-edit-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block text-sm">
                    <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Name</span>
                    <input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} data-testid="user-edit-name" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 dark:border-white/15 dark:bg-white/5 dark:text-white" />
                  </label>
                  <label className="block text-sm">
                    <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Email</span>
                    <input value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} data-testid="user-edit-email" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 dark:border-white/15 dark:bg-white/5 dark:text-white" />
                  </label>
                  <label className="block text-sm">
                    <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Role</span>
                    <select value={editForm.role} onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))} data-testid="user-edit-role" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 dark:border-white/15 dark:bg-white/5 dark:text-white">
                      {ORG_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">Organization</span>
                    <select value={editForm.org_id} onChange={(e) => setEditForm((f) => ({ ...f, org_id: e.target.value }))} data-testid="user-edit-org" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 dark:border-white/15 dark:bg-white/5 dark:text-white">
                      {orgs.map((o) => <option key={o.org_id} value={o.org_id}>{o.name}</option>)}
                    </select>
                  </label>
                </div>
                {editForm.role === "org_owner" && (
                  <p className="rounded-xl bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="user-edit-owner-warning">
                    Assigning Organization Owner will transfer ownership — this organization's current owner (if different) will be moved to Organization Admin automatically.
                  </p>
                )}
                {saveError && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="user-edit-error">{saveError}</p>}
                <div className="flex items-center gap-3">
                  <button type="button" onClick={saveEdit} disabled={saving} data-testid="user-edit-save-btn" className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">
                    {saving && <Loader2 size={14} className="animate-spin" />} Save Changes
                  </button>
                  <button type="button" onClick={() => setEditing(false)} disabled={saving} data-testid="user-edit-cancel-btn" className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">
                    Cancel
                  </button>
                </div>
              </section>
            ) : (
              <>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Name" value={detail.user.name} testId="user-detail-name" />
                  <Field label="Email" value={detail.user.email} testId="user-detail-email" />
                  <Field label="Role" value={detail.user.is_platform_admin ? "Platform Admin" : ROLE_LABELS[detail.user.role] || detail.user.role} testId="user-detail-role" />
                  <Field label="Authentication" value={authLabel(detail.user)} testId="user-detail-auth-method" />
                  <Field label="Account status" value={detail.user.status} testId="user-detail-status" />
                  <Field label="Created" value={formatDateTime(detail.user.created_at)} testId="user-detail-created" />
                  <Field label="Last login" value={formatDateTime(detail.user.last_login_at)} testId="user-detail-last-login" />
                </div>

                {canManage && (
                  <section className="flex flex-wrap items-center gap-3" data-testid="user-manage-actions">
                    <button type="button" onClick={startEdit} data-testid="user-edit-btn" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 hover:border-blue-300 hover:text-blue-600 dark:border-white/15 dark:text-slate-200 dark:hover:border-blue-500/40 dark:hover:text-blue-400">
                      <Pencil size={15} /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={toggleStatus}
                      disabled={statusBusy}
                      data-testid="user-toggle-status-btn"
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 hover:border-amber-300 hover:text-amber-600 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:border-amber-500/40 dark:hover:text-amber-400"
                    >
                      {statusBusy ? <Loader2 size={15} className="animate-spin" /> : detail.user.status === "Disabled" ? <UserCheck size={15} /> : <UserX size={15} />}
                      {detail.user.status === "Disabled" ? "Reactivate" : "Deactivate"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setDeleteConfirm(true); setDeleteError(""); }}
                      data-testid="user-delete-btn"
                      className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-4 py-2.5 text-sm font-bold text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
                    >
                      <Trash2 size={15} /> Delete
                    </button>
                  </section>
                )}

                <section>
                  {authLabel(detail.user).includes("Password") ? (
                    <>
                      <button
                        type="button"
                        onClick={sendPasswordReset}
                        disabled={resetting}
                        data-testid="user-send-password-reset-btn"
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 hover:border-blue-300 hover:text-blue-600 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:border-blue-500/40 dark:hover:text-blue-400"
                      >
                        <KeyRound size={15} /> {resetting ? "Sending…" : "Send Password Reset Link"}
                      </button>
                      {resetMessage && <p className="mt-3 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400" data-testid="user-reset-message">{resetMessage}</p>}
                    </>
                  ) : (
                    <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 dark:border-white/15 dark:bg-white/[0.03] dark:text-slate-400" data-testid="user-reset-unavailable">
                      This account signs in with Google. No Nova password reset action is available.
                    </p>
                  )}
                </section>
                <section>
                  <h3 className="text-sm font-bold text-slate-950 dark:text-white">Organization membership</h3>
                  {detail.organizations.length === 0 ? (
                    <p className="mt-3 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 dark:border-white/15 dark:bg-white/[0.03] dark:text-slate-400" data-testid="user-memberships-empty">No organization membership.</p>
                  ) : (
                    <div className="mt-3 space-y-3" data-testid="user-memberships">
                      {detail.organizations.map((org) => <div key={org.org_id} className="rounded-xl border border-slate-200 p-4 dark:border-white/10 dark:text-white"><p className="font-bold">{org.name}</p><p className="text-sm text-slate-500 dark:text-slate-400">{org.org_id}</p></div>)}
                    </div>
                  )}
                </section>
                <section>
                  <h3 className="text-sm font-bold text-slate-950 dark:text-white">Invitations</h3>
                  {detail.invitations.length === 0 ? (
                    <p className="mt-3 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 dark:border-white/15 dark:bg-white/[0.03] dark:text-slate-400" data-testid="user-invitations-empty">No invitations.</p>
                  ) : (
                    <div className="mt-3 space-y-3" data-testid="user-invitations">
                      {detail.invitations.map((invite) => <div key={invite.invite_id} className="rounded-xl border border-slate-200 p-4 dark:border-white/10 dark:text-white"><p className="font-bold">{invite.email}</p><p className="text-sm text-slate-500 dark:text-slate-400">{ROLE_LABELS[invite.role] || invite.role} · {invite.used_at ? "Accepted" : "Pending"} · {formatDateTime(invite.created_at)}</p></div>)}
                    </div>
                  )}
                </section>
              </>
            )}
          </div>
        )}
      </DetailDrawer>

      {deleteConfirm && detail && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/40 p-4" data-testid="user-delete-confirm-modal">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#111C33]">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">Delete this user?</h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              {detail.user.name || detail.user.email} will be permanently removed. The account can be restored later from "Recently deleted" if needed.
            </p>
            {deleteError && <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="user-delete-error">{deleteError}</p>}
            <div className="mt-5 flex items-center gap-3">
              <button type="button" onClick={confirmDelete} disabled={deleting} data-testid="user-delete-confirm-btn" className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50">
                {deleting && <Loader2 size={14} className="animate-spin" />} Yes, delete
              </button>
              <button type="button" onClick={() => setDeleteConfirm(false)} disabled={deleting} data-testid="user-delete-cancel-btn" className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
